import {
	Table,
	Button,
	Typography,
	Tag,
	Tooltip,
	Checkbox,
	Select,
	Modal,
	notification,
} from 'antd';
import {
	UserOutlined,
	PhoneOutlined,
	LeftOutlined,
	RightOutlined,
	ExclamationCircleOutlined,
} from '@ant-design/icons';
import { TCustomer } from '@/types/TSmartCRM';
import { EActionType } from 'src/enums/ESmartCRM';
import { useState, useMemo } from 'react';
import { CustomerModal } from '../LeadDetails/CustomerModal';

const { Text } = Typography;
const { Option } = Select;

export const TableView = ({
	customers,
	fields, // eslint-disable-line @typescript-eslint/no-unused-vars
	loading = false,
	onCustomerAction,
	pagination,
	deleteCustomer,
}) => {
	const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
	const [deletingCustomerId, setDeletingCustomerId] = useState<string | null>(null);
	const [selectedCustomer, setSelectedCustomer] = useState(null);
	const [isModalOpen, setIsModalOpen] = useState(false);

	const uniqueCustomers = useMemo(() => {
		if (!customers || !Array.isArray(customers)) {
			return [];
		}

		const uniqueMap = new Map();
		customers.forEach((customer) => {
			if (customer && customer.id && !uniqueMap.has(customer.id)) {
				uniqueMap.set(customer.id, customer);
			}
		});
		return Array.from(uniqueMap.values());
	}, [customers]);

	const handleCustomerClick = (customer: TCustomer, event: React.MouseEvent) => {
		if (event.defaultPrevented) {
			return;
		}
		setSelectedCustomer(customer);
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
		setSelectedCustomer(null);
	};

	const handleAction = (action: string, customer: TCustomer, event?: React.MouseEvent) => {
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}

		if (action === EActionType.DELETE) {
			handleDeleteCustomer(customer);
		} else if (action === EActionType.VIEW) {
			handleCustomerClick(customer, event || ({} as React.MouseEvent));
		} else {
			onCustomerAction?.(action, customer);
		}
	};

	const handleSelectCustomer = (customerId: string, checked: boolean, event: MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();

		if (checked) {
			setSelectedCustomers((prev) => [...prev, customerId]);
		} else {
			setSelectedCustomers((prev) => prev.filter((id) => id !== customerId));
		}
	};

	const handleSelectAll = (checked: boolean) => {
		if (checked) {
			setSelectedCustomers(uniqueCustomers.map((customer) => customer.id));
		} else {
			setSelectedCustomers([]);
		}
	};

	const handleDeleteCustomer = (customer: TCustomer) => {
		Modal.confirm({
			title: 'Delete Customer',
			icon: <ExclamationCircleOutlined />,
			content: 'Are you sure you want to delete this customer? This action cannot be undone.',
			okText: 'Delete',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk: async () => {
				try {
					setDeletingCustomerId(customer.id);
					await deleteCustomer(customer.id);
					notification.success({
						message: 'Success',
						description: 'Customer deleted successfully',
					});
				} catch (_error) {
					notification.error({
						message: 'Error',
						description: 'Failed to delete customer. Please try again.',
					});
				} finally {
					setDeletingCustomerId(null);
				}
			},
		});
	};

	const renderVariableValue = (value: any) => {
		if (value === null || value === undefined || value === '') {
			return '';
		}
		if (Array.isArray(value)) {
			return value.join(', ');
		}
		if (typeof value === 'boolean') {
			return value ? 'Yes' : 'No';
		}
		if (value instanceof Date || (typeof value === 'string' && !isNaN(Date.parse(value)))) {
			try {
				const date = new Date(value);
				return date.toLocaleDateString();
			} catch {
				return value.toString();
			}
		}
		return value.toString();
	};

	const renderCustomerDetails = (customer: TCustomer) => {
		const customerTags = [];

		if (customer.variables && Array.isArray(customer.variables) && customer.variables.length > 0) {
			customer.variables.forEach((variable) => {
				const { fieldName, value } = variable;
				if (value !== null && value !== undefined && value !== '') {
					const isVip =
						fieldName.toLowerCase().includes('vip') &&
						(value === true || value === 'true' || value === 'Yes');
					if (isVip) {
						customerTags.push(
							<Tooltip key={variable.id || fieldName} title={`${fieldName}: Yes`}>
								<Tag color="gold" className="mb-1">
									VIP
								</Tag>
							</Tooltip>,
						);
					} else {
						const displayValue = renderVariableValue(value);
						if (displayValue) {
							customerTags.push(
								<Tooltip key={variable.id || fieldName} title={`${fieldName}: ${displayValue}`}>
									<Tag color="blue" className="mb-1">
										{fieldName}: {displayValue}
									</Tag>
								</Tooltip>,
							);
						}
					}
				}
			});
		}

		if (customerTags.length === 0) {
			customerTags.push(
				<Tag key="new-customer" color="default" className="mb-1">
					New customer
				</Tag>,
			);
		}

		return <div className="flex flex-wrap gap-1 py-2">{customerTags}</div>;
	};

	const columns = [
		{
			title: (
				<Checkbox
					checked={
						selectedCustomers.length === uniqueCustomers.length && uniqueCustomers.length > 0
					}
					indeterminate={
						selectedCustomers.length > 0 && selectedCustomers.length < uniqueCustomers.length
					}
					onChange={(e) => handleSelectAll(e.target.checked)}
				/>
			),
			dataIndex: 'select',
			key: 'select',
			width: 50,
			render: (_: any, record: TCustomer) => (
				<Checkbox
					checked={selectedCustomers.includes(record.id)}
					onChange={(e) => handleSelectCustomer(record.id, e.target.checked, e.nativeEvent)}
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
					}}
					disabled={deletingCustomerId === record.id}
				/>
			),
		},
		{
			title: 'Customer',
			dataIndex: 'name',
			key: 'name',
			width: 300,
			render: (name: string, record: TCustomer) => (
				<div className="flex items-start space-x-3 py-2">
					<UserOutlined className="text-gray-600 mt-1" />
					<div className="min-w-0 flex-1">
						<div className="font-bold text-gray-900 truncate">{name || 'Unknown Name'}</div>
						<div className="flex items-center gap-1 mt-1">
							<PhoneOutlined className="text-gray-400 text-xs" />
							<Text className="text-gray-500 text-xs truncate">{record.number}</Text>
						</div>
						{record.email && (
							<Text className="text-gray-500 text-xs truncate block mt-1">{record.email}</Text>
						)}
					</div>
				</div>
			),
		},
		{
			title: 'Customer Details',
			key: 'smartVariables',
			render: (customer: TCustomer) => renderCustomerDetails(customer),
		},
		{
			title: 'Actions',
			key: 'actions',
			width: 120,
			render: (_: any, record: TCustomer) => {
				const isDeleting = deletingCustomerId === record.id;

				return (
					<div className="flex space-x-2 py-2">
						<Button
							type="link"
							size="small"
							className="text-blue-600 hover:text-blue-800 p-0"
							onClick={(e) => handleAction(EActionType.EDIT, record, e)}
							disabled={isDeleting}
						>
							Edit
						</Button>
						<Button
							type="link"
							size="small"
							className="text-red-600 hover:text-red-800 p-0"
							onClick={(e) => handleAction(EActionType.DELETE, record, e)}
							disabled={isDeleting}
						>
							{isDeleting ? 'Deleting...' : 'Delete'}
						</Button>
					</div>
				);
			},
		},
	];

	return (
		<>
			<div className="h-[calc(100vh-290px)] flex flex-col bg-white">
				<div className="flex-1 overflow-auto px-6">
					<Table
						dataSource={uniqueCustomers}
						columns={columns}
						loading={loading}
						rowKey="id"
						pagination={false}
						size="middle"
						className="w-full"
						tableLayout="auto"
						bordered={false}
						onRow={(record) => ({
							onClick: (event) => handleCustomerClick(record, event),
							className: 'cursor-pointer',
						})}
						rowClassName={(record) => {
							const baseClass = 'hover:bg-gray-50';
							const deletingClass = deletingCustomerId === record.id ? 'opacity-50' : '';
							return `${baseClass} ${deletingClass}`;
						}}
						scroll={{ x: 800, y: null }}
						sticky={{ offsetHeader: 0 }}
					/>
				</div>

				{pagination && (
					<div className="border-t border-gray-200 bg-white px-6 py-2 flex-shrink-0">
						<div className="hidden sm:flex justify-center items-center gap-6">
							<div className="flex items-center gap-2">
								<span className="text-sm text-gray-600 whitespace-nowrap">Rows per page:</span>
								<Select
									value={pagination.pageSize}
									onChange={pagination.changePageSize}
									size="small"
									className="w-20"
									dropdownMatchSelectWidth={false}
								>
									<Option value={10}>10</Option>
									<Option value={20}>20</Option>
									<Option value={50}>50</Option>
								</Select>
							</div>

							<div className="flex items-center gap-2">
								<Button
									icon={<LeftOutlined />}
									disabled={!pagination.hasPrev}
									onClick={pagination.prevPage}
									size="middle"
									className="flex items-center"
								>
									Prev
								</Button>
								<Button
									disabled={!pagination.hasNext}
									onClick={pagination.nextPage}
									size="middle"
									className="flex items-center"
								>
									Next
									<RightOutlined />
								</Button>
							</div>
						</div>

						<div className="flex sm:hidden justify-center items-center gap-4">
							<Select
								value={pagination.pageSize}
								onChange={pagination.changePageSize}
								size="small"
								className="w-16"
								dropdownMatchSelectWidth={false}
							>
								<Option value={10}>10</Option>
								<Option value={20}>20</Option>
								<Option value={50}>50</Option>
							</Select>

							<Button
								icon={<LeftOutlined />}
								disabled={!pagination.hasPrev}
								onClick={pagination.prevPage}
								size="small"
							/>
							<Button
								icon={<RightOutlined />}
								disabled={!pagination.hasNext}
								onClick={pagination.nextPage}
								size="small"
							/>
						</div>
					</div>
				)}
			</div>
			<CustomerModal customer={selectedCustomer} isOpen={isModalOpen} onClose={handleCloseModal} />
		</>
	);
};
